import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/database';
import 'firebase/storage';
import { firebaseConfig, firebaseProjectUrl } from '../config';
import { useGetProfileQuery } from '../redux/slices/api';
import { PATH_DASHBOARD } from '../routes/paths';

// ----------------------------------------------------------------------

// const ADMIN_EMAILS = ['demo@minimals.cc', 'yossia@gmail.com'];
if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
  firebase.firestore();
}

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  firebaseUser: null,
  role: null,
  token: null,
  profile: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user, firebaseUser, role, token } = action.payload;
    return {
      ...state,
      isAuthenticated: isAuthenticated !== undefined ? isAuthenticated : state.isAuthenticated,
      isInitialized: true,
      user: user !== undefined ? user : state.user,
      firebaseUser: firebaseUser !== undefined ? firebaseUser : state.firebaseUser,
      role: role !== undefined ? role : state.role,
      token: token !== undefined ? token : state.token,
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: 'firebase',
  getRefreshedJWT: () => Promise.resolve,
  updateUser: () => Promise.resolve,
  setUserRole: () => Promise.resolve,
  getUserWithEmail: () => Promise.resolve,
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  loginWithFaceBook: () => Promise.resolve(),
  loginWithTwitter: () => Promise.resolve(),
  loginWithMicrosoft: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  uploadFile: () => Promise.resolve(),
  getFile: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storage = firebase.storage();

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged(async (user) => {
        if (user) {
          if (user.email) {
            const firebaseUser = await getUserWithEmail(user.email);

            dispatch({
              type: 'INITIALISE',
              payload: { firebaseUser },
            });
          }
          const token = await user.getIdToken();
          const idTokenResult = await user.getIdTokenResult();
          const hasuraClaim = idTokenResult.claims['https://hasura.io/jwt/claims'];

          if (hasuraClaim) {
            dispatch({
              type: 'INITIALISE',
              payload: { isAuthenticated: true, user, role: hasuraClaim['x-hasura-default-role'] },
            });
          } else {
            // Check if refresh is required.
            const metadataRef = firebase.database().ref(`metadata/${user.uid}/refreshTime`);

            metadataRef.on('value', async (data) => {
              if (!data.exists) return;
              // Force refresh to pick up the latest custom claims changes.
              const token = await user.getIdToken(true);
              const firebaseUser = await getUserWithEmail(user.email);

              dispatch({
                type: 'INITIALISE',
                payload: { isAuthenticated: true, user: firebaseUser, firebaseUser }, // Must be modify
              });
            });
          }
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: false, user: null, role: null },
          });
        }
      }),
    [dispatch]
  );

  const auth = { ...state.user };

  const {
    data: profile,
    isError,
    refetch: refetchProfile,
  } = useGetProfileQuery(auth.uid, { skip: !auth.uid });

  const updateUser = async () => {
    await refetchProfile();
  };

  const getRefreshedJWT = async () => {
    const firebaseUser = await getUserWithEmail(auth.email);
    const token = await firebase.auth().currentUser.getIdToken(true);
    const idTokenResult = await firebase.auth().currentUser.getIdTokenResult(true);
    const hasuraClaim = idTokenResult.claims['https://hasura.io/jwt/claims'];

    if (hasuraClaim) {
      dispatch({
        type: 'INITIALISE',
        payload: {
          isAuthenticated: true,
          firebaseUser,
          user: firebaseUser,
          role: hasuraClaim['x-hasura-default-role'],
        },
      });
      const profile1 = (await refetchProfile()).data;
      return profile1;
    }
    return null;
  };

  const getUserWithEmail = async (email) => {
    if (!email) return;

    return fetch(`${firebaseProjectUrl}/processHttps/auth/getuser`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer mytoken',
      },
      body: JSON.stringify({ email }),
    })
      .then(async (response) => {
        const res = await response.json();
        if (res.error) {
          return null;
        }
        return res.user;
      })
      .catch((error) => {
        console.log('Error:', error);
      });
  };

  const updatePassword = async (uid, password) => {
    if (!uid) return;

    return fetch(`${firebaseProjectUrl}/processHttps/auth/updatepassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer mytoken',
      },
      body: JSON.stringify({ uid, password }),
    })
      .then(async (response) => {
        const res = await response.json();
        if (res.error) {
          return null;
        }
        return res.user;
      })
      .catch((error) => {
        console.log('Error:', error);
      });
  };

  const getProjectUrl = () => {
    const url = window.location.href;
    const hostname = url.split('/')[2];
    const protocol = url.split('/')[0];
    return `${protocol}//${hostname}`;
  };

  const setUserRole = async (email) => {
    if (!email) return false;
    const user = await getUserWithEmail(email);

    if (
      user &&
      (!user.customClaims ||
        user.customClaims['https://hasura.io/jwt/claims']['x-hasura-default-role'] === 'unverified')
    ) {
      try {
        const response = await fetch(`${firebaseProjectUrl}/processHttps/auth/setrole`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer mytoken',
          },
          body: JSON.stringify({ id: user.uid }),
        });

        // await updateUser();
        return true;
      } catch (error) {
        console.log('Set role error: ', error);
        return false;
      }
    }
    return false;
  };

  const login = async (email, password) => {
    const user = await getUserWithEmail(email);
    if (user) {
      // if (user.emailVerified) {
      const userCredential = await firebase.auth().signInWithEmailAndPassword(email, password);
      const currentUser = userCredential.user;

      dispatch({
        type: 'INITIALISE',
        payload: { isAuthenticated: true, user: currentUser, role: '' }, // Must be modify
      });

      return { success: true, message: 'You are logged in now' };
      // }

      // return { success: false, message: 'Please verify your email first' };
    }
    return { success: false, message: 'Wrong user or password' };
  };

  const loginWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithFaceBook = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithTwitter = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithMicrosoft = async () => {
    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(async (result) => {
        const { user, credential } = result;
        if (user) {
          if (user.emailVerified) {
            // const firstSign = await setUserRole(user.email);
            // if (firstSign) {
            //   await logout();
            //   firebase
            //     .auth()
            //     .signInWithCredential(credential)
            //     .then((result) => {
            //       dispatch({
            //         type: 'INITIALISE',
            //         payload: { isAuthenticated: true } // Must be modify
            //       });
            //     });
            // }
            dispatch({
              type: 'INITIALISE',
              payload: { isAuthenticated: true }, // Must be modify
            });
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const register = async (email, password, id, company, type) => {
    // const projectUrl = getProjectUrl();
    const user = await getUserWithEmail(email);
    /*
    if (user) {
      return {
        status: 'error',
        message: `You are already registered into our system. Please login`
      };
    }

    if (!type || (type === '2' && !id)) {
      return {
        status: 'error',
        message: `Please use the registration link in your invitation email`
      };
    }

    const userCredential = await firebase.auth().createUserWithEmailAndPassword(email, password);
    if (userCredential) {
      // Meaning that this user was invited into the company, so no need to email verify
      if (type === '2' && id) localStorage.setItem('invitationId', id);
      // const userCredential1 = await firebase.auth().signInWithEmailAndPassword(email, password);
      // const currentUser = userCredential1.user;
      // await login(email, password);
      const message = type === '2' ?
        <>
          Welcome to {company} on Earlymark Select<br />
          Please confirm joining {company}
        </>
        : 'Registered Successfully';

      return {
        status: 'success',
        message
      };
      // // Emailverified
      // const actionCodeSettings = {
      //   url: `${projectUrl}/dashboard/app`,
      //   handleCodeInApp: true
      // };
      // userCredential.user.sendEmailVerification(actionCodeSettings);
    }
    // return {
    //   success: true,
    //   message: ` We have sent you an email with instructions to verify your email,
    //             please follow the instructions and then login!`
    // };
    return {
      status: 'success',
      message: `You were registered successfully, please login`
    };
    */
    if (!user) {
      return {
        status: 'error',
        message: `You are not invited, please contact us for invitation`,
      };
    }
    console.log(user, '000');
    if (user.providerData && user.providerData.length) {
      return {
        status: 'warning',
        message: `You are already registered, please login`,
      };
    }
    const updatedUser = await updatePassword(user.uid, password);
    if (updatedUser) {
      return {
        status: 'success',
        message: `Successfully registered, please login`,
      };
    }
    return {
      status: 'error',
      message: `Failed to register`,
    };
  };

  const logout = async () => {
    await firebase.auth().signOut();
    dispatch({
      type: 'INITIALISE',
      payload: { isAuthenticated: false },
    });
  };

  const resetPassword = async (email) => {
    const projectUrl = getProjectUrl();
    const actionCodeSettings = {
      url: `${projectUrl}/dashboard/app`,
      handleCodeInApp: true,
    };
    await firebase.auth().sendPasswordResetEmail(email, actionCodeSettings);
  };

  const uploadFile = async (url) => {
    if (!url) return null;

    const user = firebase.auth().currentUser;
    if (!user) return null;

    const token = await user.getIdToken();

    return fetch(`${firebaseProjectUrl}/processHttps/general/getFile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ url }),
    })
      .then(async (response) => {
        const res = await response.json();
        return res.error ? null : res.user;
      })
      .catch((error) => {
        console.error('Error uploading file to firebase:', error);
      });
  };

  const getFile = async (path) => {
    if (!path) return null;

    try {
      const url = await storage.ref(path).getDownloadURL();
      return url;
    } catch (error) {
      console.error('Error getting file from storage:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user: {
          id: auth.uid,
          email: auth.email,
          photoURL: profile?.photo_url || auth.photoURL || '',
          displayName: profile?.display_name || auth.displayName,
          emailVerified: auth.emailVerified,
          role:
            profile?.role ||
            auth?.firebaseUser?.customClaims['https://hasura.io/jwt/claims']['x-hasura-role'] ||
            null,
          phoneNumber: profile?.phone || '',
          country: profile?.country || '',
          address: profile?.address || '',
          state: profile?.state || '',
          city: profile?.city || '',
          zipCode: profile?.postcode || '',
          about: profile?.about || '',
          isPublic: profile?.is_public || false,
          isDisabled: profile?.is_disabled || false,
          company_role: profile?.company_role || null,
          company_id:
            profile?.company_id ||
            (auth?.firebaseUser?.customClaims['https://hasura.io/jwt/claims'][
              'x-hasura-company-id'
            ] !== '-1'
              ? auth?.firebaseUser?.customClaims['https://hasura.io/jwt/claims'][
                  'x-hasura-company-id'
                ]
              : null),
          company_type: profile?.company?.type || null,
          company_divisions: profile?.company?.divisions || null,
          company_plan: profile?.company?.private_data?.plan || null,
          consultancy_id: profile?.company?.private_data?.consultancy_id || null,
          company_is_boss: profile?.company?.private_data?.is_boss || null,
          company_product_group: profile?.company?.private_data?.product_group || null,
        },
        getRefreshedJWT,
        updateUser,
        setUserRole,
        getUserWithEmail,
        login,
        register,
        loginWithGoogle,
        loginWithFaceBook,
        loginWithTwitter,
        loginWithMicrosoft,
        logout,
        resetPassword,
        uploadFile,
        getFile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
